// ═══════════════════════════════════════════════════════════════
// Portfolio.jsx — verticalstack Layer 2 Portfolio
// Path: src/components/Portfolio.jsx
//
// S33 LAYER 2 PORTFOLIO (Apr 28, 2026) — NEW FILE.
//
// PURPOSE
//   Real /portfolio render path. Replaces Layer 1 placeholder when
//   PlaceholderDashboard dispatches section === 'portfolio'.
//
// ROLE-SCOPED RENDER
//   Owner role (or 'Owner / Property Manager') → portfolio summary card
//     (parcel count, occupied units, monthly revenue placeholder) +
//     property card grid (one card per parcel).
//   PM-only role → filterable property list with search by parcelId or
//     address, click-to-detail stub.
//   Default if both → owner view (AS-Z).
//
// DATA READ
//   Parcels query: parcels where workspaceId == workspace.workspaceId.
//   Same fallback retry pattern as PlaceholderDashboard (AS-W) — orderBy
//   may require a composite index; fall back to unordered fetch on failure.
//
// CF DEPENDENCIES (AS-Q stub strategy)
//   Click-to-detail → toast 'Coming in Layer 2.5'.
//   Generate owner report → toast 'Coming in Layer 2.5'.
//   Real CF wiring deferred to Layer 2.5 alongside approval/notification
//   CF migration.
//
// VISUAL LANGUAGE (AS-V continuity, AS-X branding-deferred)
//   Inline hex literals matching PlaceholderDashboard exactly:
//     #003060 navy headings, #6B7280 muted, #9CA3AF label, #00B8D0 cyan.
//   No brand.js import — branding remains fully deferred per memory rule.
//   Same factGrid + card pattern as PlaceholderDashboard / Overview.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getVerticalLabel } from '../config/roles';

// ── Inline styles (AS-V mirror of PlaceholderDashboard) ──────────────────

const styles = {
  container: {
    maxWidth: 1100,
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif',
  },
  sectionHeader: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
    marginBottom: 4,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#6B7280',
    margin: 0,
  },
  card: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 24,
    marginBottom: 20,
  },
  cardHeader: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    marginTop: 0,
    marginBottom: 12,
  },
  factGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 20,
  },
  fact: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  factLabel: {
    fontSize: 12,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontWeight: 600,
  },
  factValue: {
    fontSize: 18,
    color: '#003060',
    fontWeight: 700,
  },

  // Owner view — property card grid
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16,
  },
  propertyCard: {
    backgroundColor: '#F8F9FA',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 16,
    cursor: 'pointer',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
  },
  propertyCardName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#003060',
    marginBottom: 6,
  },
  propertyCardAddress: {
    fontSize: 13,
    color: '#6B7280',
    marginBottom: 10,
    lineHeight: 1.4,
  },
  propertyCardMetaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 11,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontWeight: 600,
  },
  propertyCardId: {
    fontFamily: 'monospace',
    fontSize: 11,
    color: '#9CA3AF',
  },
  propertyCardPhase: {
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: 10,
  },

  // PM view — list + search
  searchRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    alignItems: 'center',
  },
  searchInput: {
    flex: 1,
    padding: '10px 14px',
    fontSize: 14,
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    fontFamily: 'Arial, sans-serif',
    color: '#1A1A1A',
    outline: 'none',
  },
  searchCount: {
    fontSize: 12,
    color: '#9CA3AF',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  pmList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  pmListItem: {
    padding: '12px 16px',
    backgroundColor: '#F8F9FA',
    borderRadius: 6,
    border: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    cursor: 'pointer',
  },
  pmListMain: {
    flex: 1,
    minWidth: 0,
  },
  pmListName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#003060',
    marginBottom: 2,
  },
  pmListAddress: {
    fontSize: 13,
    color: '#6B7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  pmListMeta: {
    fontSize: 11,
    color: '#9CA3AF',
    fontFamily: 'monospace',
    flexShrink: 0,
  },

  // Action row in summary card
  summaryActionRow: {
    display: 'flex',
    gap: 12,
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid #F3F4F6',
  },
  primaryBtn: {
    padding: '10px 16px',
    background: '#003060',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
  },
  ghostBtn: {
    padding: '10px 16px',
    background: '#FFFFFF',
    color: '#003060',
    border: '1px solid #E5E7EB',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
  },

  // States
  loadingState: {
    color: '#9CA3AF',
    fontStyle: 'italic',
    fontSize: 14,
    padding: '8px 0',
  },
  errorState: {
    color: '#B91C1C',
    fontSize: 13,
    padding: '12px 16px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    borderRadius: 6,
  },
  emptyState: {
    color: '#6B7280',
    fontSize: 14,
    fontStyle: 'italic',
    padding: '8px 0',
  },

  // Toast (AS-Q stub strategy)
  toast: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#003060',
    color: '#FFFFFF',
    padding: '10px 20px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 600,
    zIndex: 200,
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  },
};

// ── Role detection (AS-Z) ────────────────────────────────────────────────

function detectRoleView(roles, roleLabel) {
  // AS-Z: render owner view if user has owner role; otherwise PM view.
  // 'Owner / Property Manager' counts as owner. PM-only renders PM view.
  if (Array.isArray(roles)) {
    if (roles.some(r => typeof r === 'string' && r.toLowerCase().includes('owner'))) {
      return 'owner';
    }
  }
  if (typeof roleLabel === 'string' && roleLabel.toLowerCase().includes('owner')) {
    return 'owner';
  }
  return 'pm';
}

// ── Parcels loader hook (AS-W fallback retry pattern carried) ────────────

function useParcels(workspaceId) {
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!workspaceId) {
        setParcels([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const q = query(
          collection(db, 'parcels'),
          where('workspaceId', '==', workspaceId),
          orderBy('parcelId', 'asc')
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        setParcels(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        if (cancelled) return;
        console.warn('[Portfolio] Parcels query failed, retrying without orderBy:', err.message);
        try {
          const q2 = query(
            collection(db, 'parcels'),
            where('workspaceId', '==', workspaceId)
          );
          const snap2 = await getDocs(q2);
          if (cancelled) return;
          setParcels(snap2.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (err2) {
          if (cancelled) return;
          console.warn('[Portfolio] Parcels fallback also failed:', err2.message);
          setError(err2.message || 'Failed to load parcels');
          setParcels([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [workspaceId]);

  return { parcels, loading, error };
}

// ── Owner view ───────────────────────────────────────────────────────────

function OwnerPortfolioView({ workspace, parcels, loading, error, onCardClick, onGenerateReport }) {
  const verticalLabel = workspace.verticalType
    ? getVerticalLabel(workspace.verticalType)
    : 'Property Management';

  // Placeholder aggregates — real values land in Layer 2.5 with revenue/occupancy CFs.
  const parcelCount = parcels.length;
  const occupiedUnits = '—';
  const monthlyRevenue = '—';

  return (
    <>
      {/* Portfolio summary card */}
      <section style={styles.card}>
        <h2 style={styles.cardHeader}>Portfolio summary</h2>
        <div style={styles.factGrid}>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Workspace</span>
            <span style={styles.factValue}>{workspace.workspaceName || '—'}</span>
          </div>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Vertical</span>
            <span style={styles.factValue}>{verticalLabel}</span>
          </div>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Parcels</span>
            <span style={styles.factValue}>{loading ? '…' : parcelCount}</span>
          </div>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Occupied units</span>
            <span style={styles.factValue}>{occupiedUnits}</span>
          </div>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Monthly revenue</span>
            <span style={styles.factValue}>{monthlyRevenue}</span>
          </div>
        </div>

        <div style={styles.summaryActionRow}>
          <button style={styles.primaryBtn} onClick={onGenerateReport}>
            Generate owner report
          </button>
        </div>
      </section>

      {/* Property card grid */}
      <section style={styles.card}>
        <h2 style={styles.cardHeader}>Properties in this workspace</h2>

        {loading && <div style={styles.loadingState}>Loading properties…</div>}

        {!loading && error && (
          <div style={styles.errorState}>Could not load properties: {error}</div>
        )}

        {!loading && !error && parcels.length === 0 && (
          <div style={styles.emptyState}>No properties in this workspace yet.</div>
        )}

        {!loading && !error && parcels.length > 0 && (
          <div style={styles.cardGrid}>
            {parcels.map((p) => (
              <div
                key={p.id}
                style={styles.propertyCard}
                onClick={() => onCardClick(p)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#003060';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#E5E7EB';
                }}
              >
                <div style={styles.propertyCardName}>
                  {p.displayName || p.parcelId || p.id}
                </div>
                {p.address && (
                  <div style={styles.propertyCardAddress}>{p.address}</div>
                )}
                <div style={styles.propertyCardMetaRow}>
                  <span style={styles.propertyCardId}>{p.parcelId || p.id}</span>
                  <span style={styles.propertyCardPhase}>
                    {p.lifecyclePhase || '—'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

// ── PM view ──────────────────────────────────────────────────────────────

function PmPortfolioView({ workspace, parcels, loading, error, onItemClick }) {
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return parcels;
    const q = searchTerm.trim().toLowerCase();
    return parcels.filter((p) => {
      const name = (p.displayName || p.parcelId || p.id || '').toLowerCase();
      const addr = (p.address || '').toLowerCase();
      const pid = (p.parcelId || '').toLowerCase();
      return name.includes(q) || addr.includes(q) || pid.includes(q);
    });
  }, [parcels, searchTerm]);

  return (
    <section style={styles.card}>
      <h2 style={styles.cardHeader}>Properties — {workspace.workspaceName || 'workspace'}</h2>

      <div style={styles.searchRow}>
        <input
          type="text"
          placeholder="Search by parcel ID or address…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
        <span style={styles.searchCount}>
          {loading ? '…' : `${filtered.length} of ${parcels.length}`}
        </span>
      </div>

      {loading && <div style={styles.loadingState}>Loading properties…</div>}

      {!loading && error && (
        <div style={styles.errorState}>Could not load properties: {error}</div>
      )}

      {!loading && !error && parcels.length === 0 && (
        <div style={styles.emptyState}>No properties in this workspace yet.</div>
      )}

      {!loading && !error && parcels.length > 0 && filtered.length === 0 && (
        <div style={styles.emptyState}>No properties match “{searchTerm}”.</div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <ul style={styles.pmList}>
          {filtered.map((p) => (
            <li
              key={p.id}
              style={styles.pmListItem}
              onClick={() => onItemClick(p)}
            >
              <div style={styles.pmListMain}>
                <div style={styles.pmListName}>
                  {p.displayName || p.parcelId || p.id}
                </div>
                {p.address && (
                  <div style={styles.pmListAddress}>{p.address}</div>
                )}
              </div>
              <div style={styles.pmListMeta}>
                {p.lifecyclePhase || '—'}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Main Portfolio component ─────────────────────────────────────────────

export default function Portfolio({ workspace, roles, roleLabel }) {
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2800);
  };

  // AS-Q stubs — real CFs wire in Layer 2.5
  const handleCardClick = (parcel) => {
    showToast(`Property detail for ${parcel.parcelId || parcel.id} — coming in Layer 2.5`);
  };

  const handleGenerateReport = () => {
    showToast('Owner report generation — coming in Layer 2.5');
  };

  const { parcels, loading, error } = useParcels(workspace?.workspaceId);

  if (!workspace) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Loading workspace…</div>
      </div>
    );
  }

  const view = detectRoleView(roles, roleLabel);

  return (
    <div style={styles.container}>
      <header style={styles.sectionHeader}>
        <h1 style={styles.sectionTitle}>Portfolio</h1>
        <p style={styles.sectionDescription}>
          Properties and units in this workspace.
        </p>
      </header>

      {view === 'owner' ? (
        <OwnerPortfolioView
          workspace={workspace}
          parcels={parcels}
          loading={loading}
          error={error}
          onCardClick={handleCardClick}
          onGenerateReport={handleGenerateReport}
        />
      ) : (
        <PmPortfolioView
          workspace={workspace}
          parcels={parcels}
          loading={loading}
          error={error}
          onItemClick={handleCardClick}
        />
      )}

      {toast && <div style={styles.toast}>{toast}</div>}
    </div>
  );
}
